import { useState } from 'react';
import { databaseService } from '../../services/database';
import { ConfigurationRelease } from '../../types/database';

interface PromoteReleaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onPromote: () => void;
  release: ConfigurationRelease | null;
  existingPhases: string[];
}

const ALL_PHASES = ['production', 'beta', 'alpha', 'backup', 'short'];

export default function PromoteReleaseDialog({
  isOpen,
  onClose,
  onPromote,
  release,
  existingPhases,
}: PromoteReleaseDialogProps) {
  const [targetPhase, setTargetPhase] = useState('');
  const [saving, setSaving] = useState(false);

  if (!isOpen || !release) return null;

  const availablePhases = ALL_PHASES.filter((p) => !existingPhases.includes(p));

  const handlePromote = async () => {
    if (!targetPhase || !release.experience_release_id) return;
    setSaving(true);
    const result = await databaseService.upsertConfigurationRelease({
      configuration_id: release.configuration_id,
      experience_id: release.experience_id,
      experience_release_id: release.experience_release_id,
      phase: targetPhase,
    });
    if (result) {
      // Remove from the old phase so it's a move, not a copy
      await databaseService.deleteConfigurationRelease(release.id);
    }
    setSaving(false);
    if (result) {
      onPromote();
      onClose();
    }
  };

  const expName = release.experiences?.name || 'Unknown';
  const version = release.experience_release?.version || '?';

  return (
    <div className="deployment-dialog-overlay" onClick={onClose}>
      <div className="deployment-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="deployment-dialog-title">Promote Release</div>

        <div className="deployment-dialog-info">
          <strong>{expName}</strong> v{version} is currently in{' '}
          <span className={`phase-badge phase-${release.phase}`}>{release.phase}</span>
        </div>

        <div className="deployment-dialog-field">
          <label className="deployment-dialog-label">Promote to phase</label>
          <select
            className="deployment-dialog-select"
            value={targetPhase}
            onChange={(e) => setTargetPhase(e.target.value)}
          >
            <option value="">Select target phase...</option>
            {availablePhases.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        <div className="deployment-dialog-actions">
          <button className="deployment-btn" onClick={onClose}>Cancel</button>
          <button
            className="deployment-btn deployment-btn-success"
            onClick={handlePromote}
            disabled={!targetPhase || saving}
          >
            {saving ? 'Promoting...' : 'Promote'}
          </button>
        </div>
      </div>
    </div>
  );
}
